import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Edit, Loader2, PenLine, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Pagination,
  toast,
  useAuth,
} from '@aaas/shared';
import type { DataTableAction, DataTableColumn } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import {
  useDeleteEmailSignatureMutation,
  useEmailSignatureListQuery,
  useSetDefaultEmailSignatureMutation,
} from '../../hooks/useEmailSignatureQueries';
import { EmailSignatureFormDialog } from '../../components/EmailSignatureFormDialog';
import { sanitizeHtml } from '../../utils/sanitize-html';
import type { EmailSignature } from '../../types';
import { EmailsFilterSidebar } from '../emails/EmailsFilterSidebar';

export function EmailSignatureConfigPage() {
  const { t } = useTranslation('ats');
  const { user, idToken, currentWorkspace } = useAuth();
  const { page, size, setPage, setSize } = useUrlPagination();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<EmailSignature | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingSignature, setEditingSignature] = useState<EmailSignature | null>(null);

  const [previewSignature, setPreviewSignature] = useState<EmailSignature | null>(null);

  const {
    data: listResponse,
    isLoading,
    isFetching,
    refetch,
  } = useEmailSignatureListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    enabled: !!user && !!currentWorkspace,
  });

  const signatures = listResponse?.data?.items || [];
  const totalItems = listResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const deleteMutation = useDeleteEmailSignatureMutation();
  const setDefaultMutation = useSetDefaultEmailSignatureMutation();

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingSignature(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((signature: EmailSignature) => {
    setFormMode('edit');
    setEditingSignature(signature);
    setShowFormDialog(true);
  }, []);

  const handleDeleteClick = useCallback((signature: EmailSignature) => {
    setSelectedSignature(signature);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedSignature) return;
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedSignature.id));
    try {
      await deleteMutation.mutateAsync(selectedSignature.id);
      toast.success(t('emailSignature.toast.deleted'));
      setShowDeleteDialog(false);
      setSelectedSignature(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('emailSignature.toast.deleteFailed'));
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        if (selectedSignature) next.delete(selectedSignature.id);
        return next;
      });
    }
  }, [deleteMutation, selectedSignature]);

  const handleSetDefault = useCallback(
    async (signature: EmailSignature) => {
      if (signature.is_default) return;
      try {
        await setDefaultMutation.mutateAsync(signature.id);
        toast.success(t('emailSignature.toast.setDefault', { name: signature.name }));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('emailSignature.toast.setDefaultFailed'));
      }
    },
    [setDefaultMutation, t]
  );

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteDialogClose = () => {
    if (!deleteMutation.isPending) {
      setShowDeleteDialog(false);
      setSelectedSignature(null);
      setDeleteError(null);
    }
  };

  const columns: DataTableColumn<EmailSignature>[] = [
    {
      key: 'name',
      title: t('emailSignature.columnName'),
      render: (_value, record) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{record.name}</span>
          {record.is_default && (
            <span className="inline-flex items-center text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              {t('emailSignature.defaultBadge')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'raw_html',
      title: t('emailSignature.columnPreview'),
      render: (_value, record) => (
        <div
          className="prose prose-sm max-w-md text-sm text-gray-600 truncate [&_*]:truncate"
          title={t('emailSignature.openToSee')}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(record.raw_html) }}
        />
      ),
    },
    {
      key: 'updated_at',
      title: t('emailSignature.columnUpdated'),
      width: 120,
      render: (_value, record) => (
        <span className="text-sm text-gray-500">
          {new Date(record.updated_at).toLocaleDateString('vi-VN')}
        </span>
      ),
    },
  ];

  const actions: DataTableAction<EmailSignature>[] = [
    {
      key: 'view',
      label: t('emailSignature.actions.view'),
      icon: <PenLine className="h-4 w-4" />,
      onClick: (record) => setPreviewSignature(record),
      inline: true,
    },
    {
      key: 'set-default',
      label: t('emailSignature.actions.setDefault'),
      icon: <CheckCircle2 className="h-4 w-4" />,
      onClick: handleSetDefault,
      disabled: (row) => row.is_default,
    },
    {
      key: 'edit',
      label: t('emailSignature.actions.edit'),
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEditClick,
      inline: true,
    },
    {
      key: 'delete',
      label: t('emailSignature.actions.delete'),
      icon: <Trash2 className="h-4 w-4" />,
      loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
      onClick: handleDeleteClick,
      loading: (record) => deletingIds.has(record.id),
      destructive: true,
      inline: true,
    },
  ];

  return (
    <div className="flex h-[calc(100vh-64px)] bg-white rounded-lg border overflow-hidden">
      <EmailsFilterSidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <PenLine className="h-8 w-8 text-orange-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('emailSignature.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">
              {t('emailSignature.pageDescription')}
            </p>
          </div>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="h-4 w-4 mr-2" />
          {t('emailSignature.newOne')}
        </Button>
      </div>

      <div className="space-y-4">
        <DataTable
          data={signatures}
          columns={columns}
          actions={actions}
          loading={isLoading}
          emptyMessage={t('emailSignature.empty')}
          rowKey={(record) => record.id}
        />

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={size}
          onPageSizeChange={setSize}
          totalItems={totalItems}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      <EmailSignatureFormDialog
        key={`${formMode}-${editingSignature?.id ?? 'new'}-${showFormDialog}`}
        open={showFormDialog}
        onClose={() => setShowFormDialog(false)}
        mode={formMode}
        signature={editingSignature}
        onSuccess={handleFormSuccess}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={handleDeleteDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailSignature.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailSignature.deleteConfirmName', { name: selectedSignature?.name ?? '' })}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!previewSignature}
        onOpenChange={(open) => !open && setPreviewSignature(null)}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewSignature?.name}
              {previewSignature?.is_default && (
                <span className="inline-flex items-center text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-normal">
                  {t('emailSignature.defaultBadge')}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewSignature && (
            <div className="border rounded-md p-4 bg-white">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(previewSignature.raw_html),
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
